import { LitElement, html, svg, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { timelineColumns } from '../lib/timeline.js';
import { withFormats } from '../lib/table.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { apiParams } from '../lib/filters.js';
import { fillFor, patternDefs } from '../lib/patterns.js';
import { CONFIDENCE_TIERS, OTHER_SLOT, STATUS_STATES } from '../lib/palette.js';
import {
  KIND_VOCABULARY,
  ROW,
  TIMELINE_SORTS,
  barGeometry,
  comparisonOf,
  laneExportRows,
  timelineExportRows,
  timelineModel,
  timelineSummary,
  weekWidth,
  weekX,
} from '../lib/timeline.js';
import {
  hours as formatHours,
  percent,
  weekLabel,
  longDate,
  count,
  humanize,
} from '../lib/format.js';

/** The filters this view reads, in the API's names. Everything else in the bar leaves it alone. */
const PARAM_KEYS = ['run_id', 'from', 'weeks', 'dimension', 'entity', 'pipeline', 'compare_run_id'];

const SORT_LABELS = { start: 'Start', end: 'End', hours: 'Hours', name: 'Name' };

/** `1 bar`, `3 bars`. Counts in prose read as prose. */
const plural = (n, one, many = `${one}s`) => `${count(n)} ${n === 1 ? one : many}`;
const SORTS = TIMELINE_SORTS.map((key) => ({ key, label: SORT_LABELS[key] }));

/**
 * The timeline (PRD 10.2; Phase 26): the run's dated work as bars over the weeks of the window,
 * with the capacity lane above them.
 *
 * Every other view so far answers a question about a week. This one answers *when*: which
 * projects and deals occupy which weeks, which of them start where the plan said and which have
 * been pushed, and how loaded the company is in each of those weeks. The lane is the join
 * between the two readings — it is the heatmap's own row, drawn above the bars that make it up,
 * so a week that reads 118% here reads 118% there.
 *
 * A bar is where the run put its hours, not where the plan says the work is. The two agree for
 * work running to schedule; they part company for work that slipped (the plan is in August, the
 * demand is next week), for a window the tool inferred, and for a deal, which starts at its
 * kickoff. Where they part, the plan is drawn as a thin rule under the bar so both readings are
 * on screen at once, and the row states the dates.
 *
 * Encoding, PRD Appendix A, three channels and no doubling up:
 *   fill    the confidence tier — solid, hatched, dotted
 *   hue     the project type, from the shell's portfolio-wide assignment
 *   border  the record status — solid for committed work, dashed for an opportunity
 *
 * The bars are one SVG rather than one per row: the pattern defs are per document, so a
 * hundred-row grid of small SVGs would carry a hundred copies of them. Positions are
 * percentages of the plot, which is what lets the names column and the lane, both plain HTML,
 * line up with it at any width without a measured pixel anywhere.
 *
 * Pick a run in the filter bar's Compare and the ghost bars arrive (Phase 27, V-15): each bar's
 * position in that run as a dashed, unfilled outline, a rule measuring the move, and the move
 * itself labelled in weeks. The outline is achromatic on purpose — it is not a fourth encoding
 * channel, it is the same bar at an earlier date — and it is drawn only where the work is on
 * different columns now, because an outline traced under an unmoved bar says nothing twice.
 * Work the compared run put in these weeks and this run does not keeps its row, with a ghost
 * and no bar: a view for what moved must not answer by hiding what left.
 */
class ProjectTimeline extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _kinds: { state: true },
    _sort: { state: true },
    _q: { state: true },
    _week: { state: true },
    _selected: { state: true },
    _tableOpen: { state: true },
    _changedOnly: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._loading = false;
    this._error = null;
    this._kinds = KIND_VOCABULARY.map((k) => k.key);
    this._sort = 'start';
    this._q = '';
    this._week = null;
    this._selected = null;
    this._tableOpen = false;
    this._changedOnly = false;
    this._requestKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
        /* A grid item's automatic minimum is its content, and this card's content is a grid 620px
         wide: without this the card grows the page instead of scrolling inside itself. */
        min-width: 0;
      }
      .card {
        min-width: 0;
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 76ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }

      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin: 12px 0 10px;
      }
      .group {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .segmented {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented button {
        font: inherit;
        font-size: 12px;
        font-weight: 600;
        color: var(--ink-muted);
        background: none;
        border: 0;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        cursor: pointer;
      }
      .segmented button[aria-pressed='true'] {
        color: var(--surface);
        background: var(--ink-strong);
      }
      .kind {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 12.5px;
        color: var(--ink);
        cursor: pointer;
      }
      .kind input {
        accent-color: var(--ink-strong);
      }
      .search input {
        font: inherit;
        font-size: 12.5px;
        padding: 3px 8px;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        color: var(--ink);
        min-width: 170px;
      }

      .scroll {
        overflow-x: auto;
        max-width: 100%;
        padding-bottom: 4px;
      }
      .frame {
        display: grid;
        grid-template-columns: minmax(180px, 250px) 1fr;
        column-gap: 10px;
        min-width: 620px;
      }
      .corner {
        font-size: 10px;
        font-family: var(--font-eyebrow);
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--ink-muted);
        display: flex;
        align-items: center;
      }
      .cols {
        display: grid;
      }
      .weekhead button {
        font: inherit;
        font-size: 11px;
        color: var(--ink-muted);
        background: none;
        border: 0;
        border-bottom: 2px solid transparent;
        padding: 2px 0 3px;
        cursor: pointer;
        white-space: nowrap;
        overflow: hidden;
      }
      .weekhead button[data-selected='true'] {
        color: var(--ink-strong);
        font-weight: 700;
        border-bottom-color: var(--ink-strong);
      }
      .lane .cell {
        font-size: 11px;
        font-variant-numeric: tabular-nums;
        text-align: center;
        padding: 3px 0;
        border-right: 1px solid var(--surface);
        color: var(--ink-strong);
        cursor: pointer;
        white-space: nowrap;
        overflow: hidden;
      }
      .lane .cell[data-band='underload'] {
        background: var(--status-underload-tint);
      }
      .lane .cell[data-band='healthy'] {
        background: var(--status-healthy-tint);
      }
      .lane .cell[data-band='caution'] {
        background: var(--status-caution-tint);
      }
      .lane .cell[data-band='overloaded'] {
        background: var(--status-overloaded-tint);
      }
      .lane .cell .icon {
        opacity: 0.75;
        margin-right: 2px;
      }
      .lane-label {
        font-size: 11px;
        color: var(--ink-muted);
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        padding-right: 2px;
      }

      .names {
        border-top: 1px solid var(--border-soft);
      }
      .names .row {
        display: flex;
        align-items: center;
        gap: 6px;
        height: ${ROW.height}px;
        box-sizing: border-box;
        padding-right: 4px;
        overflow: hidden;
      }
      .names .row[data-selected='true'] {
        background: var(--surface-2);
      }
      .names button {
        font: inherit;
        font-size: 12.5px;
        color: var(--ink);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .names button:hover {
        color: var(--link);
        text-decoration: underline;
      }
      .names .client {
        font-size: 11px;
        color: var(--ink-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 0 1 auto;
      }
      .names .marker {
        font-size: 10px;
        color: var(--status-caution);
        flex: none;
      }

      svg.plot {
        display: block;
        width: 100%;
        border-top: 1px solid var(--border-soft);
      }
      svg.plot .gridline {
        stroke: var(--grid);
        stroke-width: 1;
      }
      svg.plot .weekmark {
        stroke: var(--ink-strong);
        stroke-width: 1;
        stroke-dasharray: 2 2;
      }
      svg.plot .rowline {
        stroke: var(--border-soft);
        stroke-width: 1;
      }
      svg.plot .rowhit {
        fill: transparent;
        cursor: pointer;
      }
      svg.plot .rowhit[data-selected='true'] {
        fill: var(--surface-2);
      }
      svg.plot .plan {
        stroke-width: 2.5;
        opacity: 0.55;
        stroke-linecap: round;
      }
      svg.plot .gap {
        fill: var(--surface);
        stroke: none;
      }
      /* The ghost: the same bar where the compared run put it. Unfilled and achromatic, so it
       cannot be read as a bar of its own or as a project type. */
      svg.plot .ghost {
        fill: none;
        stroke: var(--ink-muted);
        stroke-width: 1.25;
        stroke-dasharray: 3 2.5;
        opacity: 0.8;
      }
      /* The move itself, drawn between the two leading edges: the gap is the number. */
      svg.plot .moved {
        stroke: var(--ink-muted);
        stroke-width: 1;
        stroke-dasharray: 1 2;
        opacity: 0.8;
      }
      svg.plot .label {
        font-size: 10px;
        font-family: var(--font-sans);
        fill: var(--ink-muted);
        dominant-baseline: middle;
      }
      svg.plot .label.slip {
        fill: var(--status-caution);
        font-weight: 700;
      }
      svg.plot .label.earlier {
        fill: var(--status-healthy);
        font-weight: 700;
      }
      svg.plot .label.fresh,
      svg.plot .label.gone {
        font-weight: 600;
        font-style: italic;
      }
      svg.plot .edge {
        /* The arrow always sits on the bar it belongs to, since only a bar that reaches an edge
         can run past it: the surface colour is the one paint that reads against every hue. */
        fill: var(--surface);
        font-size: 12px;
        font-weight: 700;
        dominant-baseline: middle;
      }

      .legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 22px;
        margin-top: 12px;
      }
      .key {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 12.5px;
        color: var(--ink);
      }
      .key svg {
        display: block;
      }
      .note {
        margin: 10px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
        max-width: 88ch;
      }
      .state {
        padding: 32px 0;
        text-align: center;
        color: var(--ink-muted);
      }
      .state.error {
        color: var(--status-overloaded);
        font-weight: 600;
      }
      details.table {
        margin-top: 14px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--link);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
        color: var(--ink-muted);
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Fetching

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  /**
   * Every control on this card travels to the API rather than filtering the page in the browser,
   * because the lane states how much of each week the bars on screen account for: a card that
   * hid rows by itself would leave that figure describing rows nobody can see.
   */
  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    const params = apiParams(filters, PARAM_KEYS);
    if (this._kinds.length && this._kinds.length < KIND_VOCABULARY.length)
      params.kinds = this._kinds.join(',');
    if (filters.types.length) params.types = filters.types.join(',');
    if (filters.tiers.length) params.tiers = filters.tiers.join(',');
    if (this._q.trim()) params.q = this._q.trim();
    if (this._changedOnly && filters.compare) params.changed_only = true;
    params.sort = this._sort;
    return params;
  }

  /** Whether a run is being compared, which is what puts the ghost marks on the plot. */
  get _comparing() {
    return Boolean(this._data?.compare);
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/timeline', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
      if (this._week && !data.weeks.some((w) => w.week_start === this._week)) this._week = null;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Derived

  get _model() {
    return this._data ? timelineModel(this._data, { palette: this.appState.palette ?? [] }) : null;
  }

  _toggleKind(key) {
    const next = this._kinds.includes(key)
      ? this._kinds.filter((k) => k !== key)
      : [...this._kinds, key];
    // Nothing ticked is not a question anyone is asking; the last one stays on.
    this._kinds = next.length ? next : this._kinds;
    this._maybeFetch();
  }

  _setSort(key) {
    this._sort = key;
    this._maybeFetch();
  }

  _setQuery(value) {
    this._q = value;
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => this._maybeFetch(), 250);
  }

  _toggleChangedOnly() {
    this._changedOnly = !this._changedOnly;
    this._maybeFetch();
  }

  _selectWeek(weekStart) {
    this._week = this._week === weekStart ? null : weekStart;
  }

  _selectRow(key) {
    this._selected = this._selected === key ? null : key;
  }

  // -------------------------------------------------------------------------------------------
  // Tooltips

  _tooltip() {
    return this.renderRoot.querySelector('chart-tooltip');
  }

  _showBar(event, model, row) {
    const tooltip = this._tooltip();
    if (!tooltip) return;
    const exact = (v) => formatHours(v, { exact: true });
    const tier = CONFIDENCE_TIERS.find((t) => t.key === row.confidence_tier);
    const rows = [
      { label: 'Hours in the window', value: exact(row.hours) },
      { label: '  High confidence', value: exact(row.high_confidence_hours) },
      { label: 'Peak week', value: row.peak_week ? weekLabel(row.peak_week) : '—' },
      {
        label: row.window_start ? 'Planned window' : 'Window',
        value: row.window_start
          ? `${weekLabel(row.window_start)} – ${weekLabel(row.window_end)}`
          : 'none — a trailing baseline',
      },
      {
        label: 'Drawn from',
        value: row.bar_start
          ? `${weekLabel(row.bar_start)} – ${weekLabel(row.bar_end)}`
          : 'no hours in these weeks',
      },
    ];
    if (row.kind === 'opportunity')
      rows.push(
        { label: 'Kickoff', value: longDate(row.expected_kickoff_date) },
        { label: 'Probability', value: percent(row.probability_used) },
      );
    // The compared run, where there is one: where it put the work, and what has changed since.
    // Both figures rather than only the delta — a reviewer checking the ghost against the run of
    // a week ago needs the number that run actually held.
    if (row.ghost)
      rows.push(
        {
          label: `In the run of ${weekLabel(row.ghost.planning_date)}`,
          value: `${weekLabel(row.ghost.bar_start)} – ${weekLabel(row.ghost.bar_end)}`,
        },
        { label: '  Hours then', value: exact(row.ghost.hours) },
        {
          label: '  Change',
          value: `${row.ghost.hours_delta > 0 ? '+' : ''}${formatHours(row.ghost.hours_delta, {
            exact: true,
          })}`,
        },
      );
    const comparison = comparisonOf(row.comparison);
    const facts = [
      KIND_VOCABULARY.find((k) => k.key === row.kind)?.label,
      tier ? `${tier.label} confidence${tier.marker ? ` ${tier.marker}` : ''}` : null,
      row.schedule_source ? `window from ${humanize(row.schedule_source).toLowerCase()}` : null,
      row.slip_weeks ? `slipped ${plural(row.slip_weeks, 'week')} against its plan` : null,
      comparison && row.comparison !== 'unchanged'
        ? `${comparison.label.toLowerCase()} since the compared run`
        : null,
      row.ghost?.moved_weeks
        ? `starts ${plural(Math.abs(row.ghost.moved_weeks), 'week')} ${
            row.ghost.moved_weeks > 0 ? 'later' : 'earlier'
          }`
        : null,
      // The end on its own, where it did not move with the start: a bar whose start held and
      // whose end moved carries an outline and no label (the label is the start's move), and
      // this is the sentence that explains it.
      row.ghost?.end_moved_weeks && row.ghost.end_moved_weeks !== row.ghost.moved_weeks
        ? `ends ${plural(Math.abs(row.ghost.end_moved_weeks), 'week')} ${
            row.ghost.end_moved_weeks > 0 ? 'later' : 'earlier'
          }`
        : null,
      row.ghost?.elsewhere_in_run ? 'still forecast outside this window' : null,
      row.geometry?.continues_after ? 'continues past the window' : null,
      row.burn_risk ? 'burning faster than its schedule' : null,
    ].filter(Boolean);
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title: `${row.name}${row.number ? ` · ${row.number}` : ''}`,
      rows,
      footer: `${row.client ?? 'No client'} · ${facts.join(' · ')}`,
    });
    this._hoverWeeks = model.weeks;
  }

  _showLane(event, cell) {
    const tooltip = this._tooltip();
    if (!tooltip) return;
    const exact = (v) => formatHours(v, { exact: true });
    const state = STATUS_STATES.find((s) => s.key === cell.band);
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title: `Week of ${longDate(cell.week_start)}`,
      rows: [
        { label: 'Allocated', value: exact(cell.allocated_hours) },
        { label: '  In the bars below', value: exact(cell.drawn_hours) },
        { label: '  Not drawn as a bar', value: exact(cell.lane_only_hours) },
        { label: 'Capacity', value: exact(cell.capacity_hours) },
        cell.headroom_hours < 0
          ? { label: 'Over capacity', value: exact(-cell.headroom_hours) }
          : { label: 'Headroom', value: exact(cell.headroom_hours) },
      ],
      footer: [
        cell.utilization === null
          ? 'No capacity this week'
          : `${percent(cell.utilization)} allocated`,
        state ? `${state.icon} ${state.label}` : null,
        `${count(cell.projects)} projects · ${count(cell.opportunities)} deals`,
      ]
        .filter(Boolean)
        .join(' · '),
    });
  }

  _hide() {
    this._tooltip()?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Marks

  /**
   * The plan-level window as a thin rule under the bar, drawn only where it lands on different
   * columns than the bar does. A rule that traced the bar exactly would be two marks saying one
   * thing; a plan entirely outside the window has nowhere to be drawn, and the slip label beside
   * the bar is what says so there.
   */
  _planRule(model, row, y) {
    if (!row.window_start || !row.window_end) return nothing;
    const plan = barGeometry({ bar_start: row.window_start, bar_end: row.window_end }, model.weeks);
    if (!plan) return nothing;
    if (plan.start_index === row.geometry.start_index && plan.end_index === row.geometry.end_index)
      return nothing;
    return svg`<line class="plan" data-plan=${row.key}
      x1=${`${plan.x}%`} x2=${`${plan.x + plan.width}%`}
      y1=${y + ROW.height - 3.5} y2=${y + ROW.height - 3.5}
      stroke=${`var(${row.slot.var})`} />`;
  }

  /**
   * The ghost: where the compared run put this bar, as a dashed outline, and a rule from its
   * leading edge to the bar's, so the move is a measurable gap and not only a number. Drawn only
   * for a bar that is on different columns now, or one that has no bar left at all — the model's
   * `show_ghost` decides, so the card and the tests agree about what is on screen.
   */
  _ghost(row, y) {
    if (!row.show_ghost) return nothing;
    const gg = row.ghost.geometry;
    const g = row.geometry;
    const mid = y + ROW.height / 2;
    const top = y + (ROW.height - ROW.bar) / 2;
    return svg`
      ${
        g
          ? svg`<line class="moved" data-moved=${row.key} x1=${`${gg.x}%`} x2=${`${g.x}%`}
                      y1=${mid} y2=${mid} pointer-events="none" />`
          : nothing
      }
      <rect class="ghost" data-ghost=${row.key}
            x=${`${gg.x}%`} y=${top - 0.5} width=${`${gg.width}%`} height=${ROW.bar + 1} rx="3"
            pointer-events="none" />
    `;
  }

  /**
   * The label beside a bar. Comparing a run replaces the plan-slip label with the movement
   * between the two runs: two labels on one bar, in weeks, measured from different dates, is a
   * number nobody can read. The plan slip stays in the tooltip and in the table.
   */
  _label(row, y, right) {
    const x = `${Math.min(right + 0.6, 97)}%`;
    const at = (cls, text) =>
      svg`<text class=${`label ${cls}`} x=${x} y=${y + ROW.height / 2}
                pointer-events="none">${text}</text>`;
    if (this._comparing) {
      if (!row.comparison_label) return nothing;
      if (row.comparison === 'gone') return at('gone', 'gone');
      if (row.comparison === 'new') return at('fresh', 'new');
      return at(row.ghost.moved_weeks > 0 ? 'slip' : 'earlier', row.comparison_label);
    }
    return row.slip_weeks ? at('slip', `+${row.slip_weeks}w`) : nothing;
  }

  _bar(model, row, index) {
    const y = index * ROW.height;
    const top = y + (ROW.height - ROW.bar) / 2;
    const g = row.geometry;
    const gg = row.show_ghost ? row.ghost.geometry : null;
    const columns = model.weeks.length;
    const column = weekWidth(columns);
    const selected = this._selected === row.key;
    // The label clears whichever mark reaches furthest right, bar or ghost.
    const right = Math.max(g ? g.x + g.width : 0, gg ? gg.x + gg.width : 0);
    return svg`
      <g class="row" data-key=${row.key} data-kind=${row.kind}
         data-comparison=${row.comparison ?? 'none'}>
        <rect class="rowhit" data-selected=${selected ? 'true' : 'false'}
              x="0" y=${y} width="100%" height=${ROW.height}
              @pointermove=${(e) => this._showBar(e, model, row)}
              @pointerleave=${() => this._hide()}
              @click=${() => this._selectRow(row.key)} />
        ${this._ghost(row, y)}
        ${g ? this._planRule(model, row, y) : nothing}
        ${
          // A row the compared run drew and this one does not has no bar to draw: its ghost is
          // the whole mark, and drawing an empty rect would claim hours it does not have.
          g
            ? svg`<rect class="bar" data-key=${row.key}
                        x=${`${g.x}%`} y=${top} width=${`${g.width}%`} height=${ROW.bar} rx="3"
                        fill=${fillFor(row.slot.var, row.fill)}
                        stroke=${`var(${row.slot.var})`} stroke-width="1.5"
                        stroke-dasharray=${row.status.border === 'dashed' ? '4 2.5' : 'none'}
                        pointer-events="none" />`
            : nothing
        }
        ${row.gaps.map(
          (i) => svg`<rect class="gap" x=${`${weekX(i, columns) + column * 0.12}%`} y=${top + 1.5}
                           width=${`${column * 0.76}%`} height=${ROW.bar - 3} pointer-events="none" />`,
        )}
        ${
          // Work that runs off an edge is marked there. A glyph rather than a polygon: only x
          // takes a percentage in SVG, and every position on this plot is one.
          g?.continues_before
            ? svg`<text class="edge" x="0.2%" y=${y + ROW.height / 2}
                        pointer-events="none">◂</text>`
            : nothing
        }
        ${
          g?.continues_after
            ? svg`<text class="edge" x="99.8%" text-anchor="end" y=${y + ROW.height / 2}
                        pointer-events="none">▸</text>`
            : nothing
        }
        ${this._label(row, y, right)}
        <line class="rowline" x1="0" x2="100%" y1=${y + ROW.height} y2=${y + ROW.height} />
      </g>
    `;
  }

  _plot(model) {
    const columns = model.weeks.length;
    const height = Math.max(ROW.height, model.rows.length * ROW.height);
    const selected = this._week ? model.weeks.findIndex((w) => w.week_start === this._week) : -1;
    return html`
      <svg
        class="plot"
        height=${height}
        role="img"
        aria-label=${`${count(model.rows.length)} bars over ${count(columns)} weeks`}
        @pointerleave=${() => this._hide()}
      >
        ${unsafeSVG(patternDefs())}
        ${model.weeks.map(
          (w, i) =>
            svg`<line class="gridline" x1=${`${weekX(i, columns)}%`} x2=${`${weekX(i, columns)}%`}
                      y1="0" y2=${height} />`,
        )}
        ${
          selected >= 0
            ? svg`<line class="weekmark" x1=${`${weekX(selected, columns) + weekWidth(columns) / 2}%`}
                        x2=${`${weekX(selected, columns) + weekWidth(columns) / 2}%`} y1="0" y2=${height} />`
            : nothing
        }
        ${repeat(
          model.rows,
          (r) => r.key,
          (r, i) => this._bar(model, r, i),
        )}
      </svg>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Frame

  _weekHead(model) {
    const columns = `repeat(${model.weeks.length}, 1fr)`;
    return html`
      <div class="cols weekhead" style=${`grid-template-columns: ${columns}`}>
        ${model.weeks.map(
          (w) =>
            html`<button
              type="button"
              data-week=${w.week_start}
              data-selected=${this._week === w.week_start ? 'true' : 'false'}
              title=${`Mark the week of ${longDate(w.week_start)}`}
              @click=${() => this._selectWeek(w.week_start)}
            >
              ${weekLabel(w.week_start)}
            </button>`,
        )}
      </div>
    `;
  }

  _lane(model) {
    const columns = `repeat(${model.weeks.length}, 1fr)`;
    return html`
      <div class="cols lane" style=${`grid-template-columns: ${columns}`}>
        ${model.lane.map((cell) => {
          const state = STATUS_STATES.find((s) => s.key === cell.band);
          return html`<div
            class="cell"
            data-band=${cell.band ?? 'none'}
            data-week=${cell.week_start}
            @pointermove=${(e) => this._showLane(e, cell)}
            @pointerleave=${() => this._hide()}
            @click=${() => this._selectWeek(cell.week_start)}
          >
            ${state ? html`<span class="icon">${state.icon}</span>` : nothing}${
              cell.utilization === null ? '—' : percent(cell.utilization)
            }
          </div>`;
        })}
      </div>
    `;
  }

  _names(model) {
    return html`
      <div class="names">
        ${repeat(
          model.rows,
          (r) => r.key,
          (r) => html`
            <div
              class="row"
              data-key=${r.key}
              data-selected=${this._selected === r.key ? 'true' : 'false'}
            >
              <button
                type="button"
                title=${`${r.name}${r.number ? ` (${r.number})` : ''}`}
                @click=${() => this._selectRow(r.key)}
              >
                ${r.name}
              </button>
              <span class="client">${r.client ?? ''}</span>
              ${r.burn_risk ? html`<span class="marker" title="Burning faster than its schedule">▲</span>` : nothing}
              ${
                r.comparison === 'gone'
                  ? html`<span
                      class="marker"
                      title=${
                        r.ghost?.elsewhere_in_run
                          ? 'No hours in these weeks in this run — the work is still forecast outside the window'
                          : 'No hours in this run — only the compared run put this work here'
                      }
                      >○</span
                    >`
                  : nothing
              }
            </div>
          `,
        )}
      </div>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _kpis(summary) {
    const peak = summary.peak;
    const comparison = summary.comparison;
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${count(summary.rows)}</div>
          <div class="l">
            ${summary.rows === 1 ? 'bar' : 'bars'} · ${plural(summary.per_kind.project, 'project')},
            ${plural(summary.per_kind.recurring, 'retainer')},
            ${plural(summary.per_kind.opportunity, 'deal')}
          </div>
        </div>
        <div class="kpi">
          <div class="n">${peak ? percent(peak.utilization) : '—'}</div>
          <div class="l">peak lane${peak ? `, week of ${weekLabel(peak.week_start)}` : ''}</div>
        </div>
        ${
          // While a run is being compared the third figure is the one being asked about; the
          // plan-slip count is the same number the uncompared view shows and is one click away.
          comparison
            ? html`<div class="kpi" data-kpi="moved">
                <div class="n">${count(comparison.per_comparison.moved)}</div>
                <div class="l">
                  moved since ${weekLabel(comparison.planning_date)} ·
                  ${count(comparison.moved_later)} later, ${count(comparison.moved_earlier)}
                  earlier${
                    comparison.moved_end_only
                      ? html`, ${count(comparison.moved_end_only)} ends elsewhere`
                      : nothing
                  }
                </div>
              </div>`
            : html`<div class="kpi">
                <div class="n">${count(summary.slipped)}</div>
                <div class="l">re-anchored after slipping</div>
              </div>`
        }
      </div>
    `;
  }

  _controls() {
    return html`
      <div class="controls">
        <span class="group" role="group" aria-label="What to draw">
          ${KIND_VOCABULARY.map(
            (k) =>
              html`<label class="kind" title=${k.hint}>
                <input
                  type="checkbox"
                  data-kind=${k.key}
                  .checked=${this._kinds.includes(k.key)}
                  @change=${() => this._toggleKind(k.key)}
                />${k.plural}
              </label>`,
          )}
          ${
            // Goes to the API like every other control here, for the reason Section 15.4 gives:
            // the lane states how much of each week the bars account for.
            this._comparing
              ? html`<label
                  class="kind"
                  title="Keep only the bars that moved, changed, arrived or left since the compared run"
                >
                  <input
                    type="checkbox"
                    data-changed-only
                    .checked=${this._changedOnly}
                    @change=${() => this._toggleChangedOnly()}
                  />Changed only
                </label>`
              : nothing
          }
        </span>
        <span class="group">
          Sort
          <span class="segmented">
            ${SORTS.map(
              (s) =>
                html`<button
                  type="button"
                  data-sort=${s.key}
                  aria-pressed=${this._sort === s.key ? 'true' : 'false'}
                  @click=${() => this._setSort(s.key)}
                >
                  ${s.label}
                </button>`,
            )}
          </span>
        </span>
        <span class="group search">
          <input
            type="search"
            placeholder="Find a project, client or number"
            aria-label="Find a project, client or number"
            .value=${this._q}
            @input=${(e) => this._setQuery(e.target.value)}
          />
        </span>
      </div>
    `;
  }

  _legend(model) {
    const types = [...new Map(model.rows.map((r) => [r.slot.value ?? 'other', r.slot])).values()];
    return html`
      <div class="legend-row">
        <chart-legend
          .items=${CONFIDENCE_TIERS.map((t) => ({
            label: `${t.label} confidence`,
            // The neutral slot, which is the one non-type slot the pattern defs cover: a
            // confidence swatch must not wear a hue, or it would read as a project type.
            token: OTHER_SLOT.var,
            fill: t.fill,
          }))}
        ></chart-legend>
        <span class="key">
          <svg width="30" height="12" aria-hidden="true">
            <rect
              x="0.75"
              y="0.75"
              width="28"
              height="10"
              rx="2"
              fill="none"
              stroke="var(--ink-muted)"
              stroke-width="1.5"
              stroke-dasharray="4 2.5"
            ></rect>
          </svg>
          <span>Opportunity (dashed border)</span>
        </span>
        <span class="key">
          <svg width="30" height="8" aria-hidden="true">
            <line
              x1="0"
              x2="30"
              y1="4"
              y2="4"
              stroke="var(--ink-muted)"
              stroke-width="2"
              opacity="0.4"
            ></line>
          </svg>
          <span>Planned window, where it differs</span>
        </span>
        ${
          this._comparing
            ? html`<span class="key" data-key="ghost">
                <svg width="30" height="12" aria-hidden="true">
                  <rect
                    x="0.75"
                    y="0.75"
                    width="28"
                    height="10"
                    rx="2"
                    fill="none"
                    stroke="var(--ink-muted)"
                    stroke-width="1.25"
                    stroke-dasharray="3 2.5"
                    opacity="0.8"
                  ></rect>
                </svg>
                <span>Where the run of ${weekLabel(this._data.compare.planning_date)} had it</span>
              </span>`
            : nothing
        }
        ${
          types.length
            ? html`<chart-legend
                .items=${types.map((t) => ({ label: t.label, token: t.var, fill: 'solid' }))}
              ></chart-legend>`
            : nothing
        }
      </div>
    `;
  }

  get _columns() {
    // The specification is shared with the server's CSV (lib/timeline.js).
    const signedWeeks = (v) => (v ? `${v > 0 ? '+' : '−'}${Math.abs(v)}w` : '—');
    return withFormats(timelineColumns({ comparing: this._comparing }), {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
        week: (v) => (v ? weekLabel(v) : '—'),
        text: (v) => v ?? '—',
      },
      byKey: {
        kind: (v) => v,
        name: (v) => v,
        canonical_type: (v) => (v ? humanize(v) : '—'),
        confidence_tier: (v) => (v ? humanize(v) : '—'),
        schedule_source: (v) => (v ? humanize(v) : '—'),
        slip_weeks: (v) => (v ? `+${v}w` : '—'),
        comparison: (v) => comparisonOf(v)?.label ?? '—',
        moved_weeks: signedWeeks,
        end_moved_weeks: signedWeeks,
        hours_delta: (v) =>
          v === null || v === undefined
            ? '—'
            : `${v > 0 ? '+' : ''}${formatHours(v, { exact: true })}`,
      },
    });
  }

  _subtitle(model) {
    const d = this._data;
    const who = d.entity ? `${d.entity.label} (${d.dimension})` : 'Whole company';
    const parts = [
      who,
      `${d.weeks_count} weeks from ${weekLabel(d.from)}`,
      `pipeline ${d.pipeline_mode}`,
    ];
    if (d.compare) parts.push(`against the run of ${weekLabel(d.compare.planning_date)}`);
    if (d.truncated) parts.push(`showing ${model.rows.length} of ${d.totals.rows} bars`);
    return parts.join(' · ');
  }

  /**
   * What the comparison amounts to, in a sentence. The hours figure is the one a planner reads
   * first — the grid says which bars moved, this says whether the window got busier for it.
   */
  _comparisonNote(summary) {
    const c = summary.comparison;
    if (!c) return null;
    const per = c.per_comparison;
    const changed = per.moved + per.changed + per.new + per.gone;
    if (!changed)
      return html`Nothing has moved since the run of ${weekLabel(c.planning_date)}: every bar is on
      the same weeks with the same hours, so no outlines are drawn.`;
    const delta = formatHours(Math.abs(c.hours_delta));
    return html`Against the run of ${weekLabel(c.planning_date)}:
    ${plural(per.moved, 'bar is', 'bars are')} on different weeks (${count(c.moved_later)} starting
    later, ${count(c.moved_earlier)} starting
    earlier${
      c.moved_end_only
        ? html`, ${count(c.moved_end_only)} keeping ${c.moved_end_only === 1 ? 'its' : 'their'}
          start and ending elsewhere`
        : nothing
    }),
    ${plural(per.changed, 'has', 'have')} the same weeks and different hours, ${count(per.new)}
    ${per.new === 1 ? 'is' : 'are'} new here and ${count(per.gone)} no longer
    ${per.gone === 1 ? 'has' : 'have'} hours in these weeks. The window holds
    ${c.hours_delta === 0 ? 'the same hours' : `${delta} ${c.hours_delta > 0 ? 'more' : 'fewer'}`}
    than it did. The dashed outline is where each bar was; the label beside it is how far its start
    moved, so a bar that kept its start and changed its end carries an outline and no label — hover
    it for both figures.`;
  }

  _notes(model, summary) {
    const lane = model.lane;
    const undrawn = Math.max(0, ...lane.map((c) => c.lane_only_hours));
    const notes = [];
    const comparison = this._comparisonNote(summary);
    if (comparison) notes.push(comparison);
    if (undrawn > 0)
      notes.push(
        html`The lane counts every kind of demand; the bars are the dated work. Up to
        ${formatHours(undrawn)} in a week — the non-project baseline, and anything the controls
        above have filtered out — is in the lane with no bar to stand on.`,
      );
    if (summary.continues_after > 0)
      notes.push(
        html`${plural(summary.continues_after, 'bar runs', 'bars run')} past the end of the window
        and ${summary.continues_after === 1 ? 'is' : 'are'} drawn clipped, with the arrow at the
        edge; widen the window to see where
        ${summary.continues_after === 1 ? 'it lands' : 'they land'}.`,
      );
    if (this._data.truncated)
      notes.push(
        html`${plural(this._data.totals.rows, 'bar matches', 'bars match')}, and the
        ${count(model.rows.length)} largest by ${SORT_LABELS[this._sort].toLowerCase()} are drawn.
        Search, or narrow the filters, to see the rest.`,
      );
    return notes.map((note) => html`<p class="note">${note}</p>`);
  }

  render() {
    const { run } = this.appState;
    const model = this._model;
    const summary = model ? timelineSummary(model) : null;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!model && this._loading) body = html`<div class="state">Loading the timeline…</div>`;
    else if (!model) body = html`<div class="state">No weeks in this window.</div>`;
    else if (!model.rows.length)
      body = html`
        ${this._controls()}
        <div class="state">
          ${
            this._data.changed_only
              ? html`Nothing has moved in this window since the run of
                ${weekLabel(this._data.compare.planning_date)}. Untick "Changed only" to see the
                bars that stayed put.`
              : 'No work in this window matches. Every kind can be switched back on above.'
          }
        </div>
      `;
    else {
      body = html`
        ${this._controls()}
        <div class="scroll">
          <div class="frame">
            <div class="corner">Week commencing</div>
            ${this._weekHead(model)}
            <div class="lane-label">Allocation, all demand</div>
            ${this._lane(model)} ${this._names(model)} ${this._plot(model)}
          </div>
        </div>
        ${this._legend(model)} ${this._notes(model, summary)}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Timeline bars — ${this._subtitle(model)}`}
            export-name="timeline"
            page-size="15"
            dense
            .columns=${this._columns}
            .rows=${timelineExportRows(model)}
          ></data-table>
          <data-table
            caption=${`Capacity lane — ${this._subtitle(model)}`}
            export-name="timeline-lane"
            page-size="13"
            dense
            .columns=${[
              { key: 'week_start', label: 'Week', type: 'date', format: (v) => weekLabel(v) },
              {
                key: 'allocated_hours',
                label: 'Allocated',
                type: 'number',
                format: (v) => formatHours(v, { exact: true }),
                csv: (v) => v,
              },
              {
                key: 'capacity_hours',
                label: 'Capacity',
                type: 'number',
                format: (v) => formatHours(v, { exact: true }),
                csv: (v) => v,
              },
              {
                key: 'headroom_hours',
                label: 'Headroom',
                type: 'number',
                format: (v) => formatHours(v, { exact: true }),
                csv: (v) => v,
              },
              {
                key: 'utilization',
                label: 'Allocation',
                type: 'number',
                format: (v) => percent(v),
                csv: (v) => (v === null || v === undefined ? '' : v),
              },
              {
                key: 'band',
                label: 'State',
                type: 'text',
                format: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
                csv: (v) => v ?? '',
              },
            ]}
            .rows=${laneExportRows(model)}
          ></data-table>
        </details>
      `;
    }

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Timeline</div>
            <h2>What is running when, and what has moved?</h2>
            <div class="sub">${model ? this._subtitle(model) : ''}</div>
          </div>
          ${summary ? this._kpis(summary) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('project-timeline', ProjectTimeline);
